// Garden arches for the Living Windows, painted once on a canvas: climbing vines, leaves and each world's flowers.
const TAU = Math.PI * 2;
export const GARLAND = { padX: 0.55, padTop: 0.5, padBottom: 0.12, cw: 512, ch: 1024 };

function leafShape(g, x, y, s, rot, col, vein = true) {
  g.save();
  g.translate(x, y);
  g.rotate(rot);
  g.fillStyle = col;
  g.beginPath();
  g.moveTo(0, -s);
  g.bezierCurveTo(s * 0.6, -s * 0.45, s * 0.45, s * 0.5, 0, s);
  g.bezierCurveTo(-s * 0.45, s * 0.5, -s * 0.6, -s * 0.45, 0, -s);
  g.fill();
  if (vein) {
    g.strokeStyle = "rgba(40,70,40,0.35)";
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(0, -s * 0.85);
    g.lineTo(0, s * 0.9);
    g.stroke();
  }
  g.restore();
}

function ivy(g, x, y, s, rot) {
  [-0.9, 0, 0.9].forEach((a, i) => leafShape(g, x, y, s * (i === 1 ? 1 : 0.8), rot + a, i === 1 ? "#5F8D55" : "#6E9C5E", false));
}

function maple(g, x, y, s, rot, col) {
  g.save();
  g.translate(x, y);
  g.rotate(rot);
  g.fillStyle = col;
  g.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * TAU - Math.PI / 2;
    const r = i % 2 ? s * 0.45 : s;
    g.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  g.closePath();
  g.fill();
  g.restore();
}

function petals(g, x, y, s, n, col, inner, rot = 0) {
  g.save();
  g.translate(x, y);
  g.rotate(rot);
  for (let i = 0; i < n; i++) {
    g.save();
    g.rotate((i / n) * TAU);
    g.fillStyle = col;
    g.beginPath();
    g.ellipse(0, -s * 0.55, s * 0.32, s * 0.6, 0, 0, TAU);
    g.fill();
    g.restore();
  }
  g.fillStyle = inner;
  g.beginPath();
  g.ellipse(0, 0, s * 0.2, s * 0.2, 0, 0, TAU);
  g.fill();
  g.restore();
}

function rose(g, x, y, s, col, dark) {
  g.save();
  g.translate(x, y);
  for (let k = 0; k < 4; k++) {
    const r = s * (1 - k * 0.22);
    g.fillStyle = k % 2 ? dark : col;
    g.beginPath();
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * TAU + k * 0.6;
      g.ellipse(Math.cos(a) * r * 0.35, Math.sin(a) * r * 0.35, r * 0.55, r * 0.45, a, 0, TAU);
    }
    g.fill();
  }
  g.strokeStyle = dark;
  g.lineWidth = 1.4;
  g.beginPath();
  for (let i = 0; i < 18; i++) {
    const a = i * 0.7;
    const r = s * 0.05 + i * s * 0.018;
    g.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  g.stroke();
  g.restore();
}

function lotusBloom(g, x, y, s) {
  g.save();
  g.translate(x, y);
  for (let i = 0; i < 7; i++) {
    g.save();
    g.rotate((i - 3) * 0.34);
    const pg = g.createLinearGradient(0, 0, 0, -s);
    pg.addColorStop(0, "#F7DCE2");
    pg.addColorStop(1, "#E58AA4");
    g.fillStyle = pg;
    g.beginPath();
    g.moveTo(0, 0);
    g.bezierCurveTo(s * 0.34, -s * 0.36, s * 0.2, -s * 0.8, 0, -s);
    g.bezierCurveTo(-s * 0.2, -s * 0.8, -s * 0.34, -s * 0.36, 0, 0);
    g.fill();
    g.restore();
  }
  g.restore();
}

function lilyPad(g, x, y, s, rot) {
  g.save();
  g.translate(x, y);
  g.rotate(rot);
  g.fillStyle = "#6A9A5E";
  g.beginPath();
  g.moveTo(0, 0);
  g.ellipse(0, 0, s, s * 0.6, 0, 0.35, TAU - 0.35);
  g.closePath();
  g.fill();
  g.restore();
}

// the window's arch outline in canvas pixels; s in [0,1] runs left foot → crown → right foot
function outline(W, H, ww, wh, s) {
  const { padX, padTop, padBottom } = GARLAND;
  const gw = ww + padX * 2;
  const gh = wh + padTop + padBottom;
  const px = (x) => ((x + gw / 2) / gw) * W;
  const py = (y) => (1 - (y + padBottom) / gh) * H;
  const r = ww / 2;
  const cy = wh - r;
  const side = cy / (cy * 2 + Math.PI * r);
  let x, y, nx, ny;
  if (s < side) [x, y, nx, ny] = [-r, (s / side) * cy, -1, 0];
  else if (s > 1 - side) [x, y, nx, ny] = [r, ((1 - s) / side) * cy, 1, 0];
  else {
    const a = Math.PI - ((s - side) / (1 - 2 * side)) * Math.PI;
    [x, y, nx, ny] = [Math.cos(a) * r, cy + Math.sin(a) * r, Math.cos(a), Math.sin(a)];
  }
  const k = W / gw;
  return { x: px(x), y: py(y), nx, ny: -ny, k };
}

const STYLE = {
  lotus: { vine: "#4F7F57", leaf: ["#6A9A5E", "#7FAE6A", "#5D8C55"] },
  love: { vine: "#4E6F48", leaf: ["#5F8D55", "#6E9C5E", "#7AA466"] },
  woods: { vine: "#5A6A3E", leaf: ["#D9874A", "#C9643E", "#E3B04E", "#9DB86A"] },
};

export function drawGarland(c, id, ww, wh) {
  const g = c.getContext("2d");
  const W = c.width;
  const H = c.height;
  g.clearRect(0, 0, W, H);
  const st = STYLE[id];
  g.shadowColor = "rgba(70,45,40,0.28)";
  g.shadowBlur = 6;
  g.lineCap = "round";
  for (let v = 0; v < 3; v++) {
    g.strokeStyle = st.vine;
    g.lineWidth = 5 - v;
    g.beginPath();
    for (let i = 0; i <= 160; i++) {
      const s = i / 160;
      const o = outline(W, H, ww, wh, s);
      const off = (0.06 + 0.05 * Math.sin(s * 38 + v * 2.1)) * o.k;
      const X = o.x + o.nx * off;
      const Y = o.y + o.ny * off;
      i ? g.lineTo(X, Y) : g.moveTo(X, Y);
    }
    g.stroke();
  }
  const rnd = (() => {
    let seed = id.length * 97 + 13;
    return () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  })();
  for (let i = 0; i < 150; i++) {
    const s = rnd();
    const o = outline(W, H, ww, wh, s);
    const off = (0.02 + rnd() * 0.22) * o.k;
    const X = o.x + o.nx * off + (rnd() - 0.5) * 10;
    const Y = o.y + o.ny * off + (rnd() - 0.5) * 10;
    const rot = rnd() * TAU;
    if (id === "woods") rnd() < 0.5 ? maple(g, X, Y, 9 + rnd() * 8, rot, st.leaf[i % 4]) : ivy(g, X, Y, 8 + rnd() * 4, rot);
    else leafShape(g, X, Y, 8 + rnd() * 7, rot, st.leaf[i % 3]);
  }
  for (let t = 0; t < 7; t++) {
    const o = outline(W, H, ww, wh, t % 2 ? 0.05 + rnd() * 0.15 : 0.8 + rnd() * 0.15);
    g.strokeStyle = st.vine;
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(o.x, o.y);
    const len = 60 + rnd() * 90;
    g.bezierCurveTo(o.x + o.nx * 20, o.y + len * 0.4, o.x - o.nx * 10, o.y + len * 0.7, o.x + o.nx * 8, o.y + len);
    g.stroke();
    for (let k = 1; k < 5; k++) id === "woods" ? ivy(g, o.x + o.nx * (k % 2 ? 8 : -2), o.y + (len * k) / 5, 6, rnd() * 3) : leafShape(g, o.x + o.nx * (k % 2 ? 8 : -2), o.y + (len * k) / 5, 7, rnd() * 3, st.leaf[k % 3]);
  }
  const blooms = id === "woods" ? 0 : 26;
  for (let i = 0; i < blooms; i++) {
    const s = 0.08 + rnd() * 0.84;
    const o = outline(W, H, ww, wh, s);
    const off = (0.05 + rnd() * 0.14) * o.k;
    const X = o.x + o.nx * off;
    const Y = o.y + o.ny * off;
    if (id === "lotus") i % 3 ? lilyPad(g, X, Y, 12 + rnd() * 5, rnd() * TAU) : lotusBloom(g, X, Y, 16 + rnd() * 6);
    else i % 2 ? rose(g, X, Y, 11 + rnd() * 5, i % 4 === 1 ? "#F2A7B5" : "#E67F97", "#C95A78") : petals(g, X, Y, 9, 5, "#FFFDF8", "#F2D27A", rnd());
  }
  if (id === "woods") {
    for (let i = 0; i < 14; i++) {
      const o = outline(W, H, ww, wh, 0.25 + rnd() * 0.5);
      maple(g, o.x + o.nx * 0.12 * o.k, o.y + o.ny * 0.12 * o.k, 14 + rnd() * 6, rnd() * TAU, ["#E07A3F", "#D35C3A", "#EDB447"][i % 3]);
    }
  }
  g.shadowBlur = 0;
}

export const PETAL_COLS = { lotus: [0.95, 0.62, 0.72], love: [0.97, 0.7, 0.78], woods: [0.88, 0.5, 0.25] };
